import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, message, Tooltip, Popover, Spin, Layout, Avatar, Badge, Tabs } from 'antd';
import { 
  PlusOutlined, 
  UserOutlined, 
  LogoutOutlined,
  ReadOutlined,
  SettingOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AgentConfigService from '../services/AgentConfigService';
import sessionStorageManager from '../services/sessionStorageManager';
import SessionService from '../services/SessionService';
import { CircleDollarSign, BookUser, Settings, Book, FileText, Users, FileCheck2, Brain, Logout, BookPlus, MessageSquareMore, Cable, Blocks, Globe, DatabaseZap, ClipboardType, PackagePlus } from 'lucide-react';
import EnvVersionLabel from './Envversionlabel';
import TabNavigation from './TabNavigation';



const { Title, Text } = Typography;
const { Header, Sider, Content } = Layout;

/**
 * AgentSpaceHome - The main landing page after login showing agent categories and projects
 * 
 * @param {Object} props
 * @param {string} props.username - Current user's username
 * @param {string} props.sessionId - Current session ID
 * @param {string} props.userRole - Current user's role
 * @param {Array} props.userFeatures - User's available features/tabs
 * @param {function} props.onLogout - Logout handler function
 */
const AgentSpaceHome = ({ username, sessionId, userRole, userFeatures, tabOrder, onLogout }) => {
  const navigate = useNavigate();
  
  // State for features
  const [features, setFeatures] = useState(userFeatures || ['HUB', 'COLAB']);
  const [localTabOrder, setLocalTabOrder] = useState(tabOrder || ['HUB', 'RCM', 'COLAB', 'MCP', 'COUNSEL']);

  // State for agent configuration
  const [agentGroups, setAgentGroups] = useState({});
  const [agentConfigs, setAgentConfigs] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAgentSessions, setActiveAgentSessions] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [workspaceTitle, setWorkspaceTitle] = useState('Welcome to the CM Workspace'); // fallback default


  useEffect(() => {
    loadAgentsAndProjects();
  }, [sessionId, userRole]);  
  
  // For dynamic content fetching
  const [dynamicCardContent, setDynamicCardContent] = useState({});
  
  // For sidebar collapse state
  const [siderCollapsed, setSiderCollapsed] = useState(true);
  
  // Define a reusable style for icon circles
  const iconCircleStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'white',
    border: '2px solid #162349',
    color: '#162349',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  };
  

  useEffect(() => {
    // Load workspace title from public directory
    fetch('/title/cm-title.txt')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load title');
        }
        return response.text();
      })
      .then(text => setWorkspaceTitle(text.trim()))
      .catch(error => {
        console.warn('Could not load workspace title, using default:', error);
        // Keep the default value from state initialization
      });
  }, []);

  useEffect(() => {
    loadAgentsAndProjects();
  }, [sessionId, userRole]);

  // Refresh features on navigation (using cookie cache)
  useEffect(() => {
    const refreshFeatures = async () => {
      try {
        const mainApiUrl = await AgentConfigService.getMainApiUrl();
        const response = await fetch(`${mainApiUrl}/features`, {
          headers: { 'Authorization': `Bearer ${sessionId}` },
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setFeatures(data.features || ['HUB', 'COLAB']);
          
          // Update local tab order if provided
          if (data.tab_order) {
            setLocalTabOrder(data.tab_order);
          }
        }
      } catch (error) {
        console.warn('Failed to refresh features, using fallback:', error);
      }
    };
    
    if (sessionId && !userFeatures) {
      refreshFeatures();
    }
  }, [sessionId, userFeatures]);


  useEffect(() => {
      // Load active agent sessions
      const activeSessions = sessionStorageManager.getActiveAgentSessions(sessionId);
      setActiveAgentSessions(activeSessions);
  }, [sessionId]);



  // Load agent groups, configurations, and projects
  const loadAgentsAndProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load role-agent mapping to get the group structure
      let roleAgentMap;
      try {
        roleAgentMap = await AgentConfigService.loadRoleAgentConfig();
        console.log("Loaded role-agent mapping:", roleAgentMap);
      } catch (error) {
        console.error("Failed to load role-agent mapping:", error);
        setError("Failed to load role-agent mapping. Using default configuration.");
        
        // Use fallback configuration
        roleAgentMap = {
          "testrole": {
            "Collaborative Process Agents": ["agent100001", "agent100002"],
            "Autonomous Process Agents": ["agent100005"],
            "Knowledge Agents": ["agent100004", "agent100006"]
          }
        };
      }
      
      const userRoleData = roleAgentMap[userRole] || {};
      
      // Load all agent configs
      let allAgentConfigs;
      try {
        allAgentConfigs = await AgentConfigService.loadAllAgentConfig();
        console.log("Loaded agent configurations:", allAgentConfigs);
      } catch (error) {
        console.error("Failed to load agent configurations:", error);
        setError("Failed to load agent configurations. Using default agents.");
        
        // Use fallback configuration
        allAgentConfigs = {
          "agent100001": {
            "id": "agent100001",
            "name": "Eligibility Agent",
            "icon": "window-s4.png",
            "endpoint": "http://localhost:8011",
            "outcome_text": "All Possible Outcome"
          },
          "agent100002": {
            "id": "agent100002", 
            "name": "Service Auth Check Agent",
            "icon": "check-s4.png", 
            "endpoint": "http://localhost:8012",
            "outcome_text": "Medical Policy Criteria Check"
          },
          "agent100004": {
            "id": "agent100004",
            "name": "Medical Policy Q&A Agent",
            "icon": "books-s4.png",
            "endpoint": "http://localhost:8014",
            "outcome_text": "Medical Policy Q&A"
          },
          "agent100005": {
            "id": "agent100005",
            "name": "ASO Review Agent",
            "icon": "clipboard-s4.png",
            "endpoint": "http://localhost:8015",
            "outcome_text": "ASO Claims Review Result"
          },
          "agent100006": {
            "id": "agent100006",
            "name": "Oncology Research Assistant",
            "icon": "edit-s4.png",
            "endpoint": "http://localhost:8005",
            "outcome_text": "Oncology Research Insights"
          }
        };
      }
      
      setAgentConfigs(allAgentConfigs);
      
      // Process agent groups
      const groups = {};
      
      // Process each group based on the role-agent mapping
      try {
        Object.entries(userRoleData).forEach(([groupName, agentIds]) => {
          if (Array.isArray(agentIds)) {
            const agentsInGroup = agentIds
              .map(id => {
                const agent = allAgentConfigs[id];
                if (agent) {
                  // No need to set iconPath here since we'll determine it in renderAgentCard
                  return agent;
                }
                return null;
              })
              .filter(Boolean);
            
            // REMOVED: No more mapping needed since role_agent_config.json already uses correct names
            // Simply use the groupName as is
            if (agentsInGroup.length > 0) {
              groups[groupName] = agentsInGroup;
            }
          }
        });
        
        setAgentGroups(groups);
      } catch (error) {
        console.error("Error processing agent groups:", error);
        setError("Error processing agent groups.");
      }
      
      // Load projects
      try {
        const userProjects = await AgentConfigService.getProjects(sessionId);
        setProjects(userProjects);
        console.log("Loaded projects:", userProjects);
      } catch (error) {
        console.error("Failed to load projects:", error);
        // Don't set error for projects as it's not critical
        setProjects([]);
      }
      
    } catch (error) {
      console.error("General error in loadAgentsAndProjects:", error);
      setError("Failed to load agent configuration. Please try refreshing the page.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    await SessionService.logout(sessionId, onLogout);
  };

  // Load projects from the database
  const loadProjects = async () => {
    try {
      // In a real app, this would fetch from PostgreSQL via an API
      // For now, we'll use localStorage via sessionStorageManager
      const projectsFromStorage = sessionStorageManager.getAllProjects(sessionId);
      
      if (projectsFromStorage && projectsFromStorage.length > 0) {
        setProjects(projectsFromStorage);
      } else {
        // Mock data for demonstration
        const mockProjects = [
          {
            id: 'project1',
            name: 'ANNUAL AUDIT PROJECT',
            description: 'Annual compliance audit related claims analysis and document creation',
            created: 'May 4, 2025 11:00 AM',
            lastAccessed: 'May 12, 2025 6:54 PM',
            iconPath: '/resources/icons/proj_audit-s4.png',
            cardIcons: [
              { letter: 'i', content: 'Detailed Information about functionality' },
              { letter: 'v', content: 'Version: 1.0.2' },
              { letter: 'r', content: 'Release Notes: Fixed bug with report generation' },
              { letter: 'p', content: 'Project leads: John Smith, Jane Doe' }
            ]
          },
          {
            id: 'project2',
            name: 'CLINICAL KNOWLEDGE ACCELERATION',
            description: 'Clinical knowledge base development and curation',
            created: 'April 15, 2025 09:30 AM',
            lastAccessed: 'May 10, 2025 3:22 PM',
            iconPath: '/resources/icons/proj_knowledge-s4.png',
            cardIcons: [
              { letter: 'i', content: 'Detailed Information about functionality' },
              { letter: 'v', content: 'Version: 2.1.0' },
              { letter: 'r', content: 'Release Notes: Added new knowledge sources' },
              { letter: 'p', content: 'Project leads: Maria Johnson, Alex Chen' }
            ]
          }
        ];
        
        setProjects(mockProjects);
        
        // Store mock projects in storage manager
        mockProjects.forEach(project => {
          sessionStorageManager.setProjectInfo(sessionId, project.id, project);
        });
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      message.error('Failed to load projects');
    }
  };
  


  
  // Load dynamic content for card icons
  const loadDynamicCardContent = async () => {
    try {
      // In a real app, this would fetch from an API/database
      // For now, we'll use mock data
      const mockDynamicContent = {
        // Key is agentId
        'agent100001': {
          'usage': '237 sessions this month',
          'performance': '98.5% accuracy rate',
          'feedback': '4.8/5 user rating',
        },
        'agent100002': {
          'usage': '124 sessions this month',
          'performance': '97.2% accuracy rate',
          'feedback': '4.6/5 user rating',
        }
        // Add more agents as needed
      };
      
      setDynamicCardContent(mockDynamicContent);
    } catch (error) {
      console.error('Error loading dynamic card content:', error);
    }
  };
  
  // Handle clicking on an agent card
  const handleAgentCardClick = (agent) => {
    // Check for special agent types first
    if (agent.agent_type === 'care_prioritization') {
      navigate(`/care-prioritization/${agent.id}`);
    } 
    // Check for V3 (generic layout, no chat)
    else if (agent.chat_version === 'V3') {
      navigate(`/hub/agent/v3/${agent.id}`);  // or `/hub/agent/v3/...`
    }
    // Check for V2 chat component support
    else if (agent.chat_version === 'V2') {
      navigate(`/hub/agent/v2/${agent.id}`);
    } 
    // Default to V1 route
    else {
      navigate(`/hub/agent/${agent.id}`);
    }
  };
  

  // Helper function to determine the correct icon path
  const getIconPath = (agent) => {
    // Check if this is a user-created agent (ID starts with 'user_agent_')
    if (agent.id && agent.id.startsWith('user_agent_')) {
      return `/resources/user_agent_icons/${agent.icon || 'default.png'}`;
    }
    // Default path for system agents
    return `/resources/icons/${agent.icon || 'default.png'}`;
  };

  // Handle refresh configuration
  const handleRefreshConfig = async () => {
    try {
      setRefreshing(true);
      
      // Clear all config caches
      AgentConfigService.clearConfigCache();
      
      message.info('Refreshing configuration...');
      
      // Reload the data
      await loadAgentsAndProjects();
      
      // Also refresh features and tab order
      const mainApiUrl = await AgentConfigService.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/features`, {
        headers: { 'Authorization': `Bearer ${sessionId}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeatures(data.features || ['HUB', 'COLAB']);
        if (data.tab_order) {
          setLocalTabOrder(data.tab_order);
        }
      }
      
      message.success('Configuration refreshed!');
    } catch (error) {
      console.error('Error refreshing configuration:', error);
      message.error('Failed to refresh configuration');
    } finally {
      setRefreshing(false);
    }
  };

  // Render icon circles with tooltips/popovers
  const renderCardIcons = (icons) => {
    return (
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        {icons.map((icon, index) => (
          <Popover
            key={index}
            content={<div style={{ maxWidth: '300px' }}>{icon.content}</div>}
            title={`${icon.letter.toUpperCase()} Information`}
            trigger="hover"
          >
            <div style={iconCircleStyle}>
              {icon.letter}
            </div>
          </Popover>
        ))}
      </div>
    );
  };




  
  // Render agent card with dynamic and static content
  const renderAgentCard = (agent) => {
      console.log('Agent: ', agent)
      
      // Check if this agent has an active session
      const hasActiveSession = activeAgentSessions[agent.id];

      // Get icon data from agent configuration or use defaults
      const cardIcons = agent.card_icons || [
        { letter: 'i', content: 'Detailed Information about functionality' },
        { letter: 'v', content: `Version: ${agent.version || '1.0.0'}` },
        { letter: 'r', content: agent.help_text || 'No release notes available' },
      ];
      
      // Add dynamic content if available
      if (dynamicCardContent[agent.id]) {
        const dynamicContent = dynamicCardContent[agent.id];
        
        if (dynamicContent.usage) {
          cardIcons.push({ letter: 'u', content: dynamicContent.usage });
        }
        
        if (dynamicContent.performance) {
          cardIcons.push({ letter: 'p', content: dynamicContent.performance });
        }
        
        if (dynamicContent.feedback) {
          cardIcons.push({ letter: 'f', content: dynamicContent.feedback });
        }
      }
      
      return (
        <Card
          hoverable
          style={{
            width: '100%',
            height: '100%',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
          bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          onClick={() => handleAgentCardClick(agent)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
                          {agent.name}
                      </Title>
                      {hasActiveSession && (
                          <Tooltip title="Active session with unsaved data">
                              <Badge 
                                  dot 
                                  status="processing"
                                  style={{ marginLeft: '8px' }}
                              >
                                  <MessageSquareMore 
                                      size={24} 
                                      color="#162349"
                                      style={{ display: 'block' }}
                                  />
                              </Badge>
                          </Tooltip>
                      )}
                  </div>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                      {agent.description || 'No description available'}
                  </Text>
              </div>
              <div>
                  <img
                      src={`${getIconPath(agent)}?v=${Date.now()}`}
                      alt={agent.name}
                      style={{ 
                          width: '64px',
                          height: '64px',
                          objectFit: 'contain',
                          borderRadius: '50%',
                          backgroundColor: '#f5f5f5'
                      }}
                      onError={(e) => {
                          e.target.src = '/resources/icons/default.png';
                      }}
                  />
              </div>
          </div>
          
          <div style={{ marginTop: 'auto' }}>
            
            {/* Display two rows of icons, up to 4 per row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {cardIcons.slice(0, 4).map((icon, index) => (
                  <Popover
                    key={index}
                    content={<div style={{ maxWidth: '300px' }}>{icon.content}</div>}
                    title={`${icon.letter.toUpperCase()} `}
                    trigger="hover"
                  >
                    <div style={iconCircleStyle}>
                      {icon.letter}
                    </div>
                  </Popover>
                ))}
              </div>
              
              {cardIcons.length > 4 && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {cardIcons.slice(4, 8).map((icon, index) => (
                    <Popover
                      key={index}
                      content={<div style={{ maxWidth: '300px' }}>{icon.content}</div>}
                      title={`${icon.letter.toUpperCase()} `}
                      trigger="hover"
                    >
                      <div style={iconCircleStyle}>
                        {icon.letter}
                      </div>
                    </Popover>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      );
  };

  // Conditional tab rendering function
  const renderTabs = () => (
    <>
      {/* Onaxis Brand */}
      <div style={{
        fontSize: '28px',
        fontWeight: 'bold',
        color: 'white',
        marginRight: '20px',
        marginBottom: '-10px'
      }}>
        Agent Workspace
      </div>

      {/* Agent Hub Tab - Active */}
      {features.includes('HUB') && (
        <div 
          style={{
            padding: '0px 16px 0px 24px',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            minWidth: '220px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '-10px',
            marginBottom: '-10px'
          }}
        >
          {/*
          <img 
            src="/resources/icons/hub_active_icon.png" 
            alt="Agent Hub" 
            style={{ width: '28px', height: '28px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          */}
          <span style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: '#333'
          }}>
            AGENT HUB
          </span>              
          <Tooltip title="Refresh Configuration">
            <Button
              type="text"
              shape="circle"
              icon={<ReloadOutlined style={{ fontSize: '12px', color: '#666' }} />}
              onClick={handleRefreshConfig}
              style={{
                color: '#666',
                border: '1px solid #ddd',
                minWidth: '18px',
                height: '18px'
              }}
            />
          </Tooltip>
        </div>
      )}

      {/* Agent Colab Tab - Inactive */}
      {features.includes('COLAB') && (
        <div 
          style={{
            padding: '0px 16px 0px 24px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            cursor: 'pointer',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            minWidth: '220px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '-10px',
            marginBottom: '-10px',
            marginLeft: '4px',
            transition: 'all 0.3s ease'
          }}
          onClick={() => navigate('/agent-colab')}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
          }}
        >
          {/*
          <img 
            src="/resources/icons/colab_inactive_icon.png" 
            alt="Agent Colab" 
            style={{ width: '28px', height: '28px', opacity: 0.9 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          */}
          <span style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            AGENT COLAB
          </span>
        </div>
      )}

      {/* MCP Tab - Future */}
      {features.includes('MCP') && (
        <div 
          style={{
            padding: '0px 16px 0px 24px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            cursor: 'pointer',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            minWidth: '220px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '-10px',
            marginBottom: '-10px',
            marginLeft: '4px',
            transition: 'all 0.3s ease'
          }}
          onClick={() => navigate('/mcp')}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
          }}
        >
          {/*
          <img 
            src="/resources/icons/mcp_inactive_icon.png" 
            alt="MCP" 
            style={{ width: '28px', height: '28px', opacity: 0.9 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          */}
          <span style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            MCP LAUNCHPAD
          </span>
        </div>
      )}

      {/* COUNSEL Tab - Future */}
      {features.includes('COUNSEL') && (
        <div 
          style={{
            padding: '0px 16px 0px 24px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            cursor: 'pointer',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            minWidth: '220px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '-10px',
            marginBottom: '-10px',
            marginLeft: '4px',
            transition: 'all 0.3s ease'
          }}
          onClick={() => navigate('/counsel')}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
          }}
        >
          {/*
          <img 
            src="/resources/icons/counsel_inactive_icon.png" 
            alt="Agent Counsel" 
            style={{ width: '28px', height: '28px', opacity: 0.9 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          */}
          <span style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            AGENT COUNSEL
          </span>
        </div>
      )}
    </>
  );

  
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading Agent Space...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#f5222d' }}>
        <div style={{ fontSize: '18px', marginBottom: '8px' }}>Error</div>
        <div>{error}</div>
      </div>
    );
  }
  
  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Left Sidebar */}
      <Sider 
        width={60} 
        collapsible 
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{ 
          //background: '#162349',
          display: 'flex', 
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10
        }}
      >
        <div style={{ 
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          height: '100%',
          alignItems: 'center',
          padding: '20px 0'
        }}>
          {/* Top icons would go here */}
          <div></div>
          
          {/* Bottom user and logout icons - MINIMAL AGENT HUB SIDEBAR */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            

            <Tooltip title={username} placement="right">
              <Avatar 
                style={{ 
                  backgroundColor: 'white', 
                  color: '#162349', 
                  cursor: 'pointer',
                  border: '2px solid white'
                }}
                icon={<UserOutlined />} 
              />
            </Tooltip>
            
            <Tooltip title="Logout" placement="right">
              <Button 
                type="text" 
                icon={<LogoutOutlined style={{ color: 'white' }} />} 
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
          <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: "Bold" }} />
          </div>
        </div>
      </Sider>
      
      <Layout style={{ marginLeft: '60px' }}> {/* Add margin to account for fixed Sider */}
        {/* Top Header */}
        <Header style={{ 
          background: '#162349', 
          padding: '8px 24px 0 24px',
          height: 'auto',
          color: 'white'
        }}>
          {/* Onaxis Brand and Tab Navigation */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0' }}>
            <TabNavigation 
              features={features}
              activeTab="HUB"
              tabOrder={localTabOrder}
              onRefresh={handleRefreshConfig}
              refreshing={false}
            />
          </div>
        </Header>

        {/* Main Content */}
        <Content style={{ padding: '24px', 
        //background: '#f0f2f5' ,
        background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)'
        }}>

          {/* Welcome Header */}
          <Title level={1} style={{ marginBottom: '40px', fontSize: '28px' }}>
            {workspaceTitle}
          </Title>


          {/* Agent Categories */}
          {Object.entries(agentGroups).map(([groupName, groupAgents]) => (
            <div key={groupName} style={{ marginBottom: '32px' }}>
              <Title level={2} style={{ marginBottom: '16px', fontSize: '22px' }}>
                {groupName}
              </Title>
              
              <Row gutter={[24, 24]}>
                {groupAgents.map(agent => (
                  <Col key={agent.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                    {renderAgentCard(agent)}
                  </Col>
                ))}
              </Row>
            </div>
          ))}
          
          {/* Projects */}

        </Content>
      </Layout>
    </Layout>
  );
};

export default AgentSpaceHome;